import { sanitizeExportedString } from "./sanitize";
export class NodeJsonExporter {
  constructor(_node, dispatcher) {
    this._node = _node;
    this.dispatcher = dispatcher;
  }
  data(options = {}) {
    var _a, _b, _c, _d, _e, _f, _g;
    if (!this.is_root()) {
      this._node.scene().nodesController.registerNodeContextSignature(this._node);
    }
    this._data = {
      type: this._node.type()
    };
    const nodes_data = this.nodes_data(options);
    if (Object.keys(nodes_data).length > 0) {
      this._data["nodes"] = nodes_data;
    }
    const params_data = this.params_data();
    if (Object.keys(params_data).length > 0) {
      this._data["params"] = params_data;
    }
    if (!this.is_root()) {
      if (this._node.io.inputs.maxInputsCountOverriden()) {
        this._data["maxInputsCount"] = this._node.io.inputs.maxInputsCount();
      }
      const inputs_data = this.inputs_data();
      if (inputs_data.length > 0) {
        this._data["inputs"] = inputs_data;
      }
      const connection_points_data = this.connection_points_data();
      if (connection_points_data) {
        this._data["connection_points"] = connection_points_data;
      }
    }
    if (this._node.flags) {
      const flags_data = {};
      if (this._node.flags.hasBypass() || this._node.flags.hasDisplay() || this._node.flags.hasOptimize()) {
        if (this._node.flags.hasBypass()) {
          if ((_a = this._node.flags.bypass) == null ? void 0 : _a.active()) {
            flags_data["bypass"] = this._node.flags.bypass.active();
          }
        }
        if (this._node.flags.hasDisplay()) {
          if (((_b = this._node.flags.display) == null ? void 0 : _b.active()) || !((_c = this._node.parent()) == null ? void 0 : _c.displayNodeController)) {
            flags_data["display"] = (_d = this._node.flags.display) == null ? void 0 : _d.active();
          }
        }
        if (this._node.flags.hasOptimize()) {
          if ((_e = this._node.flags.optimize) == null ? void 0 : _e.active()) {
            flags_data["optimize"] = (_f = this._node.flags.optimize) == null ? void 0 : _f.active();
          }
        }
      }
      if (Object.keys(flags_data).length > 0) {
        this._data["flags"] = flags_data;
      }
    }
    if (this._node.childrenAllowed()) {
      const selection = (_g = this._node.childrenController) == null ? void 0 : _g.selection;
      if (selection && this._node.children().length > 0) {
        const selected_children = [];
        const selected_ids = {};
        for (let selected_node of selection.nodes()) {
          selected_ids[selected_node.graphNodeId()] = true;
        }
        for (let child of this._node.children()) {
          if (child.graphNodeId() in selected_ids) {
            selected_children.push(child);
          }
        }
        const selection_data = selected_children.map((n) => n.name());
        if (selection_data.length > 0) {
          this._data["selection"] = selection_data;
        }
      }
    }
    if (this._node.io.inputs.overrideClonedStateAllowed()) {
      const overriden = this._node.io.inputs.clonedStateOverriden();
      if (overriden) {
        this._data["cloned_state_overriden"] = overriden;
      }
    }
    if (this._node.persisted_config) {
      const persisted_config_data = this._node.persisted_config.toJSON();
      if (persisted_config_data) {
        this._data.persisted_config = persisted_config_data;
      }
    }
    this.add_custom();
    return this._data;
  }
  uiData(options = {}) {
    const data = this.ui_data_without_children();
    const children = this._node.children();
    if (children.length > 0) {
      data["nodes"] = {};
      children.forEach((child) => {
        const node_exporter = this.dispatcher.dispatchNode(child);
        data["nodes"][child.name()] = node_exporter.uiData(options);
      });
    }
    return data;
  }
  ui_data_without_children() {
    const data = {};
    if (!this.is_root()) {
      const ui_data = this._node.uiData;
      data["pos"] = ui_data.position().toArray();
      const comment = ui_data.comment();
      if (comment) {
        data["comment"] = sanitizeExportedString(comment);
      }
    }
    return data;
  }
  is_root() {
    return this._node.parent() === null && this._node.graphNodeId() == this._node.root().graphNodeId();
  }
  inputs_data() {
    const data = [];
    this._node.io.inputs.inputs().forEach((input, input_index) => {
      var _a;
      if (input) {
        const connection = this._node.io.connections.inputConnection(input_index);
        if (this._node.io.inputs.hasNamedInputs()) {
          const output_index = connection.output_index;
          const output_name = (_a = input.io.outputs.namedOutputConnectionPoints()[output_index]) == null ? void 0 : _a.name();
          if (output_name) {
            data[input_index] = {
              index: input_index,
              node: input.name(),
              output: output_name
            };
          }
        } else {
          data[input_index] = input.name();
        }
      }
    });
    return data;
  }
  connection_points_data() {
    if (!this._node.io.has_connection_points_controller) {
      return;
    }
    if (!this._node.io.connection_points.initialized()) {
      return;
    }
    if (this._node.io.inputs.hasNamedInputs() || this._node.io.outputs.hasNamedOutputs()) {
      const data = {};
      if (this._node.io.inputs.hasNamedInputs()) {
        data["in"] = [];
        for (let cp of this._node.io.inputs.namedInputConnectionPoints()) {
          if (cp) {
            data["in"].push(cp.toJSON());
          }
        }
      }
      if (this._node.io.outputs.hasNamedOutputs()) {
        data["out"] = [];
        for (let cp of this._node.io.outputs.namedOutputConnectionPoints()) {
          if (cp) {
            data["out"].push(cp.toJSON());
          }
        }
      }
      return data;
    }
  }
  params_data() {
    const data = {};
    for (let param_name of this._node.params.names) {
      const param = this._node.params.get(param_name);
      if (param && !param.parentParam()) {
        const param_exporter = this.dispatcher.dispatchParam(param);
        if (param_exporter.required()) {
          const params_data = param_exporter.data();
          data[param.name()] = params_data;
        }
      }
    }
    return data;
  }
  nodes_data(options = {}) {
    const data = {};
    for (let child of this._node.children()) {
      const node_exporter = this.dispatcher.dispatchNode(child);
      data[child.name()] = node_exporter.data(options);
    }
    return data;
  }
  add_custom() {
  }
}
